/**
 * A folder somebody pointed at, as a row of the project document (§5.25, §15).
 *
 * The reading itself is the app's — `describeRepo` in `@airlock/contracts` — so
 * what is here is only the translation between what a repository is on *a
 * machine* and what a project is in *a document*, and the two differ in exactly
 * the places that matter:
 *
 * - **A repository is identified by its remote, not by its folder.** The app
 *   stores an absolute path, because the checkout is right there; a document
 *   cannot, because it is read by people who do not have it. So `.git/config` is
 *   read and put through `normalizeRemote`, and a repository with no remote is
 *   reported as the one gap that stops a project working at all.
 * - **A secret's value does not travel.** Detection finds `DATABASE_URL` in
 *   somebody's `.env` and its value with it. The document keeps a secret as
 *   `value: null` and its value sealed apart, under this organization's own key
 *   — so the name is carried here and the value is deliberately dropped rather
 *   than shipped into a document every member reads. The page says which ones,
 *   because a variable that arrived empty looks like a detection failure.
 * - **A folder each member picks.** `relPath` is where this repository lands
 *   under it, which is the path inside the folder that was pointed at.
 *
 * Everything else is a prefill the person edits before publishing, exactly as it
 * is in the app: nothing here is the last word.
 */
import { describeRepo, type DetectedRepo } from '@airlock/contracts/workspace/detect';
import { MAX_ENV_VARS } from '@airlock/contracts/workspace/document';
import { httpsCloneUrl, normalizeRemote } from '@airlock/contracts/workspace/remote';
import { gitIdentity, type GitIdentity } from '../../lib/git-dir';
import type { FoundRepo } from '../../lib/repo-folder';
import type { DocProject } from '../../lib/types';

/** What one folder turned out to be, before it is written onto a row. */
export interface RepoReading {
  name: string;
  relPath: string;
  detected: DetectedRepo;
  identity: GitIdentity;
}

export interface Prefilled {
  repo: DocProject;
  /**
   * What the reading could not answer, in the words the person needs to act on
   * it. Empty is the good case and the page says nothing at all then.
   */
  gaps: string[];
  /** Variables whose name was carried and whose value was not. */
  sealed: string[];
}

/** Read one repository: its own files, and the two in its `.git`. */
export async function readRepo(found: FoundRepo): Promise<RepoReading> {
  const [detected, config, head] = await Promise.all([
    describeRepo(found.reader),
    found.reader.read('.git/config'),
    found.reader.read('.git/HEAD')
  ]);
  return { name: found.name, relPath: found.relPath, detected, identity: gitIdentity(config, head) };
}

/**
 * The reading written onto a row.
 *
 * A patch onto an existing row rather than a row of its own, so the id, the
 * colour and the sort order stay whoever made them — reading a folder into a
 * project that already exists has to keep it the same project.
 *
 * The clone URL is the https one on the three hosts that have a sign-in of their
 * own (`httpsCloneUrl`), and otherwise the URL the publisher's own remote spells
 * — which is the app's rule too (§5.16): a member cloning from GitHub uses their
 * GitHub account, and an `ssh://` URL would ask them for a key instead.
 */
/**
 * Names whose value is a credential. The app stopped guessing on 2026-10-02 —
 * there the person marks a secret in a list on their own machine — but here the
 * value is about to be written into a document every member reads, so a name
 * that looks like a credential is sealed until somebody says otherwise.
 */
const SECRETISH =
  /SECRET|TOKEN|PASSW|_PWD\b|API_?KEY|PRIVATE|CREDENTIAL|SALT|SIGNATURE|_AUTH|AUTH_|(DATABASE|POSTGRES|MYSQL|MONGO|REDIS|AMQP)_?(URL|URI|DSN)/i;

export function prefilled(base: DocProject, reading: RepoReading): Prefilled {
  const { detected, identity } = reading;
  const remote = identity.remoteUrl ? normalizeRemote(identity.remoteUrl) : null;
  const env = detected.env.slice(0, MAX_ENV_VARS).map((one) => ({ ...one, isSecret: one.isSecret || SECRETISH.test(one.key) }));
  const sealed = env.filter((one) => one.isSecret).map((one) => one.key);

  const repo: DocProject = {
    ...base,
    name: reading.name.slice(0, 80),
    remote: remote?.remote ?? '',
    host: remote?.host ?? 'other',
    cloneUrl: remote ? (httpsCloneUrl(remote) ?? remote.cloneUrl) : '',
    relPath: reading.relPath,
    defaultBranch: identity.branch ?? 'main',
    runtime: detected.runtime,
    kind: detected.kind,
    packageManager: detected.packageManager,
    installCmd: detected.installCmd,
    startCmd: detected.startCmd,
    checkCmd: detected.checkCmd,
    healthCheck: detected.healthCheck,
    port: detected.port,
    envFile: detected.envFile,
    env: env.map((one) => ({
      key: one.key,
      // `null` exactly when secret — the document's own rule, and the reason the
      // value read off this machine goes no further than this function.
      value: one.isSecret ? null : one.value,
      isSecret: one.isSecret,
      // The only scope there is now: a variable reaches install and start alike.
      // Written because an older app's parser requires the key (`EnvScope`).
      scope: 'both'
    }))
  };

  return { repo, gaps: gapsIn(reading, remote !== null), sealed };
}

function gapsIn(reading: RepoReading, addressable: boolean): string[] {
  const gaps: string[] = [];
  if (!addressable) {
    gaps.push(
      reading.identity.remoteUrl
        ? `Its remote — ${reading.identity.remoteUrl} — is not one another machine could clone, so the clone URL is empty. Push it somewhere first.`
        : 'No remote could be read from its .git, so the clone URL is empty. A member who has no checkout clones from it, so it needs one.'
    );
  }
  // Nothing is said about a repository that named no technology: the empty
  // runtime and the empty commands are the whole of that fact, and a line
  // repeating them back was a paragraph about fields the reader is looking at.
  if (reading.detected.runtime !== 'unknown' && !reading.detected.startCmd) {
    gaps.push('Nothing in it said how it is started, so the run command is empty.');
  }
  if (!reading.identity.branch) {
    gaps.push('Its HEAD is not on a branch — detached, or nothing committed yet — so the default branch reads main.');
  }
  return gaps;
}
